/**
 * @fileoverview NotesSummaryCard — collapsible AI summary block shown
 * above a chronological contact notes feed.
 *
 * Scope-agnostic and purely presentational — shared by the job-contact
 * and firm-contact feeds. The card has three states:
 * - **No summary yet** — render nothing (the "Summarise with AI" button
 *   in the parent header drives generation).
 * - **Cached + fresh** — render the summary text, a "Last summarised X
 *   ago" caption, and a Refresh button.
 * - **Cached + stale** — same as fresh but with a warning pill noting
 *   that new notes have been added since the summary was generated.
 *
 * The card never decides when to (re-)summarise; it always reflects the
 * `summary` prop from the parent. Generation is driven by the parent via
 * the action because we want one source of truth for the busy/error state
 * across the header button and this card.
 */

import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";

interface NotesSummary {
  text: string;
  generatedAt: number;
  noteCount: number;
}

interface NotesSummaryCardProps {
  summary: NotesSummary | null;
  /** Live count of notes for the contact — used to detect a stale cache. */
  liveNoteCount: number;
  /** True while the parent is invoking the summarise action. */
  isGenerating: boolean;
  /** Triggered by the Refresh button — parent re-runs with `force: true`. */
  onRefresh: () => void;
  className?: string;
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}

export function NotesSummaryCard({
  summary,
  liveNoteCount,
  isGenerating,
  onRefresh,
  className,
}: NotesSummaryCardProps) {
  if (!summary) return null;

  const isStale = summary.noteCount !== liveNoteCount;
  const noteCountLabel = `${summary.noteCount} note${summary.noteCount === 1 ? "" : "s"}`;

  return (
    <div
      className={cn(
        "rounded-lg border border-amber-200/60 bg-amber-50/60 p-3 dark:border-amber-900/40 dark:bg-amber-950/20",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-100/80 dark:bg-amber-900/40">
            <Sparkles className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400" />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
              AI summary
            </span>
            <span className="text-[10px] text-muted-foreground">
              {formatRelative(summary.generatedAt)} · across {noteCountLabel}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {isStale && (
            <Badge
              color="amber"
              variant="soft"
              size="sm"
              className="text-[10px]"
            >
              Out of date
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isGenerating}
            className="h-7 gap-1 px-2 text-[11px] text-amber-700 hover:bg-amber-100/60 dark:text-amber-400 dark:hover:bg-amber-900/30"
            title="Regenerate summary"
          >
            {isGenerating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Refresh
          </Button>
        </div>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
        {summary.text}
      </p>
    </div>
  );
}
